import React, { useContext, useMemo } from 'react';
import { HolderOutlined } from '@ant-design/icons';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Table, Tooltip, Tag } from 'antd';
import EditIcon from '@mui/icons-material/Edit';
import { IconButton, Box, Typography } from '@mui/material';
//import CustomCheckbox from '../CommonComponents/CustomCheckbox';
import CustomCheckbox from '../../../CommonComponents/CustomCheckbox';

const RowContext = React.createContext({});

const DragHandle = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: 'move' }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

const Row = props => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props['data-row-key'] });
  
  const { parameters, getParameterBackgroundColor } = props;
  const parameter = parameters?.find(p => p.id === props['data-row-key']);
  const rowBackgroundColor = parameter && getParameterBackgroundColor ? 
    getParameterBackgroundColor(parameter) : undefined;

  const style = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    ['backgroundColor']: rowBackgroundColor,
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };
  
  const contextValue = useMemo(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners],
  );
  
  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

const ParameterTable = ({ 
  parameters, 
  onReorder, 
  onEdit, 
  deleteMode, 
  onDeleteCheck, 
  deleteParameterArr,
  // New props for the additional select column
  showAdditionalSelect,
  additionalSelectTitle,
  additionalSelectArr = [],
  onAdditionalSelectCheck,
  theme,
  getDeviceName,
  getTagName,
  getParameterBackgroundColor
}) => {
  console.log("Rendering ParameterTable with additionalSelectArr:", additionalSelectArr);
  const columns = [
    { 
      key: 'sort', 
      align: 'center', 
      width: 60, 
      render: () => <DragHandle /> 
    },
    // Delete/Remove selection column
    ...(deleteMode ? [{
      title: 'Select',
      width: 120,
      render: (_, record) => (
        <CustomCheckbox
          checked={deleteParameterArr.includes(record.id)}
          onChange={(checked) => onDeleteCheck(record.id, checked)}
          width={18}
          height={18}
        />
      ),
    }] : []),
    // // Additional selection column (for other purposes)
    // ...(showAdditionalSelect ? [{
    //   title: additionalSelectTitle,
    //   width: 140,
    //   render: (_, record) => (
    //     <CustomCheckbox
    //       checked={additionalSelectArr.includes(record.id)}
    //       onChange={(checked) => onAdditionalSelectCheck(record.id, checked)}
    //       width={18}
    //       height={18}
    //     />
    //   ),
    // }] : []),
    {
      title: 'Parameter Name',
      dataIndex: 'name',
      width: 200,
      render: (text) => (
        <Typography
          sx={{
            fontWeight: 600,
            color: theme === 'light' ? '#3C3C3C' : '#C1C1C1',
            fontSize: '16px'
          }}
        >
          {text}
        </Typography>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'parameterType',
      width: 100,
      render: (type) => (
        <Tag color={type === 'tag' ? 'blue' : type === 'target' ? 'green' : 'orange'}>
          {type.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Configuration',
      width: 300,
      render: (_, record) => (
        <Box>
          {record.parameterType === 'tag' && record.selectedTags && record.selectedTags.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 500, color: theme === 'light' ? '#666' : '#aaa' }}>
                Selected Tags ({record.selectedTags.length}):
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                {Object.entries(
                  record.selectedTags.reduce((acc, tag) => {
                    acc[tag.deviceId] = acc[tag.deviceId] || { deviceName: tag.deviceName, tags: [] };
                    acc[tag.deviceId].tags.push(tag);
                    return acc;
                  }, {})
                ).slice(0, 2).map(([deviceId, data], idx) => (
                  <Box key={idx} sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 500, color: '#008CD7' }}>
                      {data.deviceName}:
                    </Typography>
                    <Box sx={{ ml: 1 }}>
                      {data.tags.slice(0, 3).map((tag, tagIdx) => (
                        <Tag key={tagIdx} size="small" color="blue">
                          {tag.tagName}
                        </Tag>
                      ))}
                      {data.tags.length > 3 && (
                        <Tag size="small">+{data.tags.length - 3} more</Tag>
                      )}
                    </Box>
                  </Box>
                ))}
                {Object.keys(record.selectedTags.reduce((acc, tag) => {
                  acc[tag.deviceId] = true;
                  return acc;
                }, {})).length > 2 && (
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    +{Object.keys(record.selectedTags.reduce((acc, tag) => {
                      acc[tag.deviceId] = true;
                      return acc;
                    }, {})).length - 2} more devices
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          
          {record.parameterType === 'target' && (
            <Box>
              <Typography variant="body2">
                {record.targetSelection.yearlyBaseline && 'Yearly Baseline '}
                {record.targetSelection.target && 'Target '}
                {record.targetSelection.manualValue && `(${record.targetSelection.manualValue} ${record.targetSelection.manualUnit})`}
              </Typography>
            </Box>
          )}
          
          {record.parameterType === 'manual' && (
            <Typography variant="body2">
              Value: {record.targetSelection.manualValue} {record.targetSelection.manualUnit}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      title: 'Settings',
      width: 150,
      render: (_, record) => (
        <Box>
          {record.aggregation && (
            <Tag size="small" color="purple">{record.aggregation}</Tag>
          )}
          {record.timePeriod && (
            <Tag size="small" color="cyan">{record.timePeriod}</Tag>
          )}
          {record.showInCollapsedMode && (
            <Tag size="small" color="gold">Collapsed</Tag>
          )}
          {record.showAsCriticalParameter && (
            <Tag size="small" color="red">Critical</Tag>
          )}
          {record.SelectedParameter && (
            <Tag size="small" color="yellow">Selected</Tag>
          )}
        </Box>
      ),
    },
    ...(deleteMode ? [] : [{
      title: 'Action',
      width: 80,
      render: (_, record) => (
        <Tooltip title="Edit">
          <IconButton 
            onClick={() => onEdit(record)}
            sx={{ 
              color: theme === 'light' ? '#008CD7' : '#40a9ff',
              padding: '4px'
            }}
          >
            <EditIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        </Tooltip>
      ),
    }]),
  ];

  const onDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      const activeIndex = parameters.findIndex(record => record.id === active?.id);
      const overIndex = parameters.findIndex(record => record.id === over?.id);
      const newParameters = arrayMove(parameters, activeIndex, overIndex);
      onReorder(newParameters);
    }
  };

  return (
    <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
      <SortableContext items={parameters.map(i => i.id)} strategy={verticalListSortingStrategy}>
        <Table
          rowKey="id"
          components={{ body: {  row: (props) => <Row {...props} parameters={parameters} getParameterBackgroundColor={getParameterBackgroundColor} /> } }}
          columns={columns}
          dataSource={parameters}
          pagination={false}
          size="middle"
          scroll={{ x: 800 }}
          style={{
            backgroundColor: theme === 'light' ? '#ffffff' : '#333333',
          }}
        />
      </SortableContext>
    </DndContext>
  );
};

export default ParameterTable;